import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Button, LoadingState, Modal } from '@/components/common';
import * as notificationsApi from '@/api/notifications';
import type { TeamNotification } from '@/types/api';

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string | null | undefined;
  currentUserEmail: string | null | undefined;
  onUnseenChange?: (hasUnseen: boolean) => void;
}

const CREATOR_EMAIL = 'andrew.winter@trueclassic.com';
const DIRECT_VIDEO_RE = /\.(mp4|webm|ogg|mov)(\?.*)?$/i;

type VideoPreview =
  | { type: 'video'; src: string; label: string }
  | { type: 'embed'; src: string; label: string }
  | { type: 'link'; src: string; label: string };

function getHostnameLabel(url: URL): string {
  return url.hostname.replace(/^www\./, '');
}

function getVideoPreview(urlValue: string): VideoPreview | null {
  try {
    const url = new URL(urlValue);
    const hostname = url.hostname.replace(/^www\./, '');
    const path = url.pathname;

    if (
      DIRECT_VIDEO_RE.test(urlValue) ||
      (hostname.endsWith('cloudinary.com') && path.includes('/video/'))
    ) {
      return { type: 'video', src: urlValue, label: getHostnameLabel(url) };
    }

    if (hostname === 'youtu.be') {
      const id = path.split('/').filter(Boolean)[0];
      if (id) {
        return {
          type: 'embed',
          src: `https://www.youtube-nocookie.com/embed/${id}`,
          label: 'YouTube',
        };
      }
    }

    if (
      hostname === 'youtube.com' ||
      hostname === 'm.youtube.com' ||
      hostname === 'youtube-nocookie.com'
    ) {
      const watchId = url.searchParams.get('v');
      const pathParts = path.split('/').filter(Boolean);
      const embedId =
        watchId ||
        (pathParts[0] === 'embed' ? pathParts[1] : null) ||
        (pathParts[0] === 'shorts' ? pathParts[1] : null);
      if (embedId) {
        return {
          type: 'embed',
          src: `https://www.youtube-nocookie.com/embed/${embedId}`,
          label: 'YouTube',
        };
      }
    }

    if (hostname === 'loom.com') {
      const parts = path.split('/').filter(Boolean);
      const id = parts[0] === 'share' || parts[0] === 'embed' ? parts[1] : null;
      if (id) {
        return {
          type: 'embed',
          src: `https://www.loom.com/embed/${id}`,
          label: 'Loom',
        };
      }
    }

    return { type: 'link', src: urlValue, label: getHostnameLabel(url) };
  } catch {
    return null;
  }
}

function formatNotificationDate(value: string | null): string {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NotificationsModal({
  isOpen,
  onClose,
  teamId,
  currentUserEmail,
  onUnseenChange,
}: NotificationsModalProps) {
  const [notifications, setNotifications] = useState<TeamNotification[]>([]);
  const [canCreateFromApi, setCanCreateFromApi] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [post, setPost] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);

  const isCreatorEmail = (currentUserEmail || '').toLowerCase() === CREATOR_EMAIL;
  const canCreate = isCreatorEmail && canCreateFromApi;

  const sortedNotifications = useMemo(
    () =>
      [...notifications].sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      }),
    [notifications]
  );

  useEffect(() => {
    if (!isOpen) {
      setComposerOpen(false);
      return;
    }
    if (!teamId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    notificationsApi
      .listTeamNotifications(teamId)
      .then((response) => {
        if (cancelled) return;
        setNotifications(response.notifications);
        setCanCreateFromApi(response.can_create);
        onUnseenChange?.(response.has_unseen);
        if (response.notifications.length > 0) {
          notificationsApi
            .markTeamNotificationsSeen(teamId)
            .then(() => onUnseenChange?.(false))
            .catch(() => undefined);
        }
      })
      .catch((err: { detail?: string }) => {
        if (cancelled) return;
        setError(err.detail || 'Failed to load notifications.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!teamId || !canCreate || !post.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const created = await notificationsApi.createTeamNotification(teamId, {
        post,
        video_url: videoUrl || null,
      });
      setNotifications((current) => [created, ...current]);
      setPost('');
      setVideoUrl('');
      setComposerOpen(false);
      onUnseenChange?.(false);
      void notificationsApi.markTeamNotificationsSeen(teamId).catch(() => undefined);
    } catch (err) {
      const apiError = err as { detail?: string };
      setError(apiError.detail || 'Failed to create notification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Notifications"
      size="lg"
      className="notifications-modal"
    >
      <div className="notifications-modal__content">
        <div className="notifications-modal__toolbar">
          <span className="notifications-modal__count">
            {sortedNotifications.length} announcement{sortedNotifications.length === 1 ? '' : 's'}
          </span>
          {canCreate && !composerOpen && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="notifications-modal__new-button"
              onClick={() => setComposerOpen(true)}
              leftIcon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              }
            >
              New
            </Button>
          )}
        </div>

        {canCreate && composerOpen && (
          <form className="notifications-composer" onSubmit={handleSubmit}>
            <div className="notifications-composer__header">
              <strong>New announcement</strong>
              <button
                type="button"
                className="icon-button icon-button--sm"
                aria-label="Close composer"
                onClick={() => {
                  setComposerOpen(false);
                  setPost('');
                  setVideoUrl('');
                }}
              >
                ×
              </button>
            </div>
            <div className="form-field">
              <label className="form-label" htmlFor="notification-post">
                Announcement
              </label>
              <textarea
                id="notification-post"
                className="form-input notifications-composer__textarea"
                value={post}
                onChange={(event) => setPost(event.target.value)}
                placeholder="Write a company update..."
                maxLength={5000}
                required
              />
            </div>

            <div className="notifications-composer__footer">
              <div className="form-field notifications-composer__video-field">
                <label className="form-label" htmlFor="notification-video-url">
                  Video URL
                </label>
                <input
                  id="notification-video-url"
                  className="form-input"
                  type="url"
                  value={videoUrl}
                  onChange={(event) => setVideoUrl(event.target.value)}
                  placeholder="Optional"
                />
              </div>
              <Button
                type="submit"
                size="sm"
                isLoading={isSubmitting}
                disabled={!post.trim() || isSubmitting}
              >
                Publish
              </Button>
            </div>
            {videoUrl.trim() && (
              <NotificationVideoPreview url={videoUrl.trim()} compact />
            )}
          </form>
        )}

        {error && <div className="notifications-modal__error">{error}</div>}

        {isLoading ? (
          <LoadingState message="Loading notifications..." />
        ) : sortedNotifications.length === 0 ? (
          <div className="notifications-empty">
            <span>No notifications yet.</span>
          </div>
        ) : (
          <div className="notifications-list">
            {sortedNotifications.map((notification) => (
              <article className="notification-card" key={notification.id}>
                <header className="notification-card__header">
                  <div>
                    <strong>
                      {notification.created_by.name ||
                        notification.created_by.email ||
                        'True Classic'}
                    </strong>
                    <span>{formatNotificationDate(notification.created_at)}</span>
                  </div>
                </header>
                <p className="notification-card__post">{notification.post}</p>
                {notification.video_url && (
                  <NotificationVideoPreview url={notification.video_url} />
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function NotificationVideoPreview({
  url,
  compact = false,
}: {
  url: string;
  compact?: boolean;
}) {
  const preview = getVideoPreview(url);
  if (!preview) {
    return null;
  }

  const className = compact
    ? 'notification-video-preview notification-video-preview--compact'
    : 'notification-video-preview';

  if (preview.type === 'video') {
    return (
      <div className={className}>
        <div className="notification-video-preview__label">{preview.label}</div>
        <video controls preload="metadata" src={preview.src} />
      </div>
    );
  }

  if (preview.type === 'embed') {
    return (
      <div className={className}>
        <div className="notification-video-preview__label">{preview.label}</div>
        <iframe
          src={preview.src}
          title={`${preview.label} video preview`}
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <a
      className="notification-video-link"
      href={preview.src}
      target="_blank"
      rel="noreferrer"
    >
      <span>Open video</span>
      <small>{preview.label}</small>
    </a>
  );
}

export default NotificationsModal;
